import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

export default function OfferedPatientsTooltip({
  patients,
  scheduleLabel,
}: {
  patients: Array<{ name: string; schedulingPageViewed: boolean }>;
  scheduleLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const tooltipId = useId();

  useEffect(() => {
    if (!open) return;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node) && !tooltipRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    const closeOnMove = (event: Event) => {
      if (event.target instanceof Node && tooltipRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", closeOnMove, true);
    window.addEventListener("resize", closeOnMove);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeOnMove, true);
      window.removeEventListener("resize", closeOnMove);
    };
  }, [open]);

  if (patients.length === 0) return null;

  const patientLabel = `${patients.length} ${patients.length === 1 ? "patient" : "patients"}`;

  return (
    <span ref={containerRef} className="relative ml-auto inline-flex shrink-0">
      <button
        type="button"
        className="inline-flex h-6 min-w-6 items-center justify-center rounded-full border border-violet-200 bg-violet-50 px-1.5 text-xs font-semibold text-violet-700 hover:border-violet-300 hover:bg-violet-100 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-1 dark:border-violet-900/70 dark:bg-violet-950/50 dark:text-violet-300 dark:hover:border-violet-800 dark:hover:bg-violet-950"
        aria-label={`${scheduleLabel} has been offered to ${patientLabel}`}
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onClick={() => {
          const bounds = containerRef.current?.getBoundingClientRect();
          if (bounds) setPosition({ top: Math.max(8, Math.min(bounds.bottom + 8, window.innerHeight - 272)), left: Math.max(8, Math.min(bounds.right - 256, window.innerWidth - 264)) });
          setOpen((current) => !current);
        }}
      >
        {patients.length}
      </button>
      {open ? createPortal(
        <span
          ref={tooltipRef}
          id={tooltipId}
          role="tooltip"
          style={position}
          className="fixed z-50 max-h-64 w-64 overflow-auto rounded-lg border border-neutral-200 bg-white p-3 text-left text-sm font-normal leading-5 text-neutral-600 shadow-lg dark:border-neutral-800 dark:bg-neutral-950 dark:text-neutral-300"
        >
          <span className="block font-semibold text-neutral-900 dark:text-white">Offered to</span>
          <span className="mt-1 block space-y-1">
            {patients.map((patient) => (
              <span key={patient.name} className="flex items-center justify-between gap-3">
                <span>{patient.name}</span>
                <span className={patient.schedulingPageViewed ? "text-emerald-700 dark:text-emerald-300" : "text-amber-700 dark:text-amber-300"}>
                  {patient.schedulingPageViewed ? "Viewed" : "Not viewed"}
                </span>
              </span>
            ))}
          </span>
        </span>, document.body
      ) : null}
    </span>
  );
}
